import React, { useState, useEffect } from 'react';
import { Plus, RefreshCw, ToggleLeft, ToggleRight, Trash2, X, Search } from 'lucide-react';
import api from '../../services/api';

interface BookingXPItem {
  id: number;
  partner_id: number;
  source: 'MANUAL';
  is_active: boolean;
  partner_name: string;
  category: string;
  experience_title: string;
  benefit_description: string;
  location_description: string;
  cover_image_url: string;
  effective_discount: number | null;
  effective_policy: string;
  effective_limit: number | null;
  redemption_count: number;
  override_discount_percentage: number | null;
  override_redemption_policy: string | null;
  override_redemption_limit: number | null;
}

interface XPPartner {
  id: number;
  name: string;
  category: string;
  benefit_description: string;
  default_discount_percentage: number | null;
  default_redemption_policy: string;
  is_active: boolean;
}

const POLICY_LABELS: Record<string, string> = {
  ONCE: 'Once per trip',
  ONCE_PER_DAY: 'Once per day',
  PER_TRIP_LIMIT: 'Limited uses',
  UNLIMITED: 'Unlimited',
};

interface Props {
  portalId: number;
}

const BookingXPTab: React.FC<Props> = ({ portalId }) => {
  const [manualItems, setManualItems] = useState<BookingXPItem[]>([]);
  const [globalPartners, setGlobalPartners] = useState<XPPartner[]>([]);
  const [excludedIds, setExcludedIds] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [allPartners, setAllPartners] = useState<XPPartner[]>([]);
  const [search, setSearch] = useState('');
  const [adding, setAdding] = useState(false);

  useEffect(() => { load(); }, [portalId]);

  const load = async () => {
    setLoading(true);
    try {
      const [manualRes, globalRes, exclusionRes] = await Promise.all([
        api.get(`/xp/booking/${portalId}`),
        api.get('/xp/global'),
        api.get(`/xp/booking/${portalId}/exclusions`),
      ]);
      if (manualRes.data.success) setManualItems(manualRes.data.data.filter((i: BookingXPItem) => i.source === 'MANUAL'));
      if (globalRes.data.success) setGlobalPartners(globalRes.data.data);
      if (exclusionRes.data.success) setExcludedIds(new Set(exclusionRes.data.excluded_ids));
    } catch (e) {
      console.error('Error loading booking XP:', e);
    } finally {
      setLoading(false);
    }
  };

  const toggleGlobal = async (partner: XPPartner) => {
    const isExcluded = excludedIds.has(partner.id);
    try {
      if (isExcluded) {
        await api.delete(`/xp/booking/${portalId}/exclude/${partner.id}`);
        setExcludedIds(prev => { const s = new Set(prev); s.delete(partner.id); return s; });
      } else {
        await api.post(`/xp/booking/${portalId}/exclude/${partner.id}`);
        setExcludedIds(prev => new Set(prev).add(partner.id));
      }
    } catch (e) {
      console.error('Error toggling global XP:', e);
    }
  };

  const openAddModal = async () => {
    try {
      const res = await api.get('/xp/partners');
      if (res.data.success) setAllPartners(res.data.data.filter((p: XPPartner) => p.is_active));
    } catch (e) {
      console.error('Error loading partners:', e);
    }
    setSearch('');
    setShowAddModal(true);
  };

  const toggle = async (item: BookingXPItem) => {
    try {
      await api.patch(`/xp/booking/${portalId}/xp/${item.id}`, { is_active: !item.is_active });
      load();
    } catch (e) {
      console.error('Error toggling XP:', e);
    }
  };

  const remove = async (item: BookingXPItem) => {
    if (!window.confirm(`Remove "${item.partner_name}" from this booking?`)) return;
    try {
      await api.delete(`/xp/booking/${portalId}/xp/${item.id}`);
      load();
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Error removing XP');
    }
  };

  const assign = async (partner: XPPartner) => {
    setAdding(true);
    try {
      await api.post(`/xp/booking/${portalId}`, { partner_id: partner.id });
      setShowAddModal(false);
      load();
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Error assigning XP');
    } finally {
      setAdding(false);
    }
  };

  const assignedIds = new Set(manualItems.map(i => i.partner_id));

  const filteredPartners = allPartners.filter(p =>
    !assignedIds.has(p.id) &&
    (p.name.toLowerCase().includes(search.toLowerCase()) ||
     p.category.toLowerCase().includes(search.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-5 h-5 animate-spin text-gray-400" />
        <span className="ml-2 text-gray-500 text-sm">Loading XP...</span>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-5">

      {/* Global Deals */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div>
            <h3 className="text-sm font-semibold text-gray-800">🌍 Global Deals</h3>
            <p className="text-xs text-gray-400">Shown to all guests by default. Toggle off to hide for this guest.</p>
          </div>
          <button onClick={load} className="p-1.5 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100">
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>

        {globalPartners.length === 0 ? (
          <p className="text-xs text-gray-400 py-3 text-center border border-dashed border-gray-200 rounded-lg">
            No global XP partners configured yet. Add partners in XP → XP Partners.
          </p>
        ) : (
          <div className="space-y-2">
            {globalPartners.map(partner => {
              const excluded = excludedIds.has(partner.id);
              return (
                <div key={partner.id} className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border ${
                  excluded ? 'border-gray-100 bg-gray-50 opacity-50' : 'border-gray-200 bg-white'
                }`}>
                  <button onClick={() => toggleGlobal(partner)} className="flex-shrink-0">
                    {excluded
                      ? <ToggleLeft className="w-5 h-5 text-gray-300" />
                      : <ToggleRight className="w-5 h-5 text-green-500" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-800 truncate">{partner.name}</span>
                      <span className="px-1.5 py-0.5 bg-indigo-50 text-indigo-600 text-xs rounded-full flex-shrink-0">{partner.category}</span>
                    </div>
                    <div className="text-xs text-gray-400 truncate">
                      {partner.benefit_description || '—'}
                      {partner.default_discount_percentage && <span className="ml-1 text-green-600 font-medium">· {partner.default_discount_percentage}% off</span>}
                      <span className="ml-1">· {POLICY_LABELS[partner.default_redemption_policy] || partner.default_redemption_policy}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Guest Specific */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div>
            <h3 className="text-sm font-semibold text-gray-800">⭐ Guest Specific</h3>
            <p className="text-xs text-gray-400">Manually added for this guest only.</p>
          </div>
          <button
            onClick={openAddModal}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white text-xs rounded-lg hover:bg-indigo-700"
          >
            <Plus className="w-3.5 h-3.5" /> Add Deal
          </button>
        </div>

        {manualItems.length === 0 ? (
          <p className="text-xs text-gray-400 py-3 text-center border border-dashed border-gray-200 rounded-lg">
            No guest-specific deals added yet.
          </p>
        ) : (
          <div className="space-y-2">
            {manualItems.map(item => (
              <XPRow key={item.id} item={item} onToggle={toggle} onRemove={remove} />
            ))}
          </div>
        )}
      </div>

      {/* Add Deal Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-sm font-semibold text-gray-900">Add Guest-Specific Deal</h2>
              <button onClick={() => setShowAddModal(false)} className="p-1 text-gray-400 hover:text-gray-600">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-3 border-b border-gray-100">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input
                  className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Search partners..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  autoFocus
                />
              </div>
            </div>
            <div className="overflow-y-auto flex-1 p-2">
              {filteredPartners.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-6">
                  {allPartners.filter(p => !assignedIds.has(p.id)).length === 0
                    ? 'All active partners are already assigned to this booking.'
                    : 'No partners match your search.'}
                </p>
              ) : (
                filteredPartners.map(p => (
                  <button
                    key={p.id}
                    onClick={() => assign(p)}
                    disabled={adding}
                    className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-indigo-50 flex items-center justify-between group"
                  >
                    <div>
                      <div className="text-sm font-medium text-gray-800">{p.name}</div>
                      <div className="text-xs text-gray-400">{p.category} · {p.benefit_description || '—'}</div>
                    </div>
                    <Plus className="w-4 h-4 text-indigo-400 opacity-0 group-hover:opacity-100" />
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// ── XP Row ────────────────────────────────────────────────────────

const XPRow: React.FC<{
  item: BookingXPItem;
  onToggle: (item: BookingXPItem) => void;
  onRemove: (item: BookingXPItem) => void;
}> = ({ item, onToggle, onRemove }) => (
  <div className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border ${
    item.is_active ? 'border-gray-200 bg-white' : 'border-gray-100 bg-gray-50 opacity-60'
  }`}>
    <button onClick={() => onToggle(item)} className="flex-shrink-0">
      {item.is_active
        ? <ToggleRight className="w-5 h-5 text-green-500" />
        : <ToggleLeft className="w-5 h-5 text-gray-300" />
      }
    </button>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-gray-800 truncate">{item.partner_name}</span>
        <span className="px-1.5 py-0.5 bg-indigo-50 text-indigo-600 text-xs rounded-full flex-shrink-0">
          {item.category}
        </span>
      </div>
      <div className="text-xs text-gray-400 truncate">
        {item.benefit_description || '—'}
        {item.effective_discount && <span className="ml-1 text-green-600 font-medium">· {item.effective_discount}% off</span>}
        <span className="ml-1">· {POLICY_LABELS[item.effective_policy] || item.effective_policy}</span>
        {item.redemption_count > 0 && (
          <span className="ml-1 text-amber-600">· {item.redemption_count} redeemed</span>
        )}
      </div>
    </div>
    {item.source === 'MANUAL' && (
      <button
        onClick={() => onRemove(item)}
        className="flex-shrink-0 p-1 text-gray-300 hover:text-red-500 rounded"
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    )}
  </div>
);

export default BookingXPTab;
